import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey, SystemProgram, SYSVAR_RENT_PUBKEY, TransactionInstruction } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { AnchorProvider, BN, Idl, Program } from '@project-serum/anchor';
import pumpProgramIdl from '../utils/pump-program-idl';
import { FEE_RECIPIENT_PUBKEY, GLOBAL_PUBKEY, PROGRAM_PUBKEY, TOKEN_DECIMALS } from '../utils/pump-fun';
import AnchorWallet from '../utils/anchor-wallet';

export const usePump = () => {
    const _useProgram = (connection: Connection, wallet: Keypair, fn: (program: Program<Idl>) => any) =>
        fn(new Program(pumpProgramIdl, PROGRAM_PUBKEY, new AnchorProvider(connection, new AnchorWallet(wallet), AnchorProvider.defaultOptions())));

    const getBuyInstruction = (
        connection: Connection,
        wallet: Keypair,
        mint: PublicKey,
        bondingCurve: PublicKey,
        associatedBondingCurve: PublicKey,
        ata: PublicKey,
        solAmount: number,
        slippage: number,
    ): Promise<TransactionInstruction> =>
        _useProgram(connection, wallet, async (program) => {
            const liquidityPool = await program.account.bondingCurve.fetch(bondingCurve, 'processed');

            const lamportsAmount = Math.floor(solAmount * LAMPORTS_PER_SOL);

            const tokenAmount = getTokenAmount(lamportsAmount, liquidityPool);

            const maxSolAmount = slippage == 100 ? new BN(100 * LAMPORTS_PER_SOL) : new BN(lamportsAmount).mul(new BN(100 + slippage)).div(new BN(100));

            return program.methods
                .buy(tokenAmount, maxSolAmount)
                .accounts({
                    global: GLOBAL_PUBKEY,
                    feeRecipient: FEE_RECIPIENT_PUBKEY,
                    mint: mint,
                    bondingCurve: new PublicKey(bondingCurve),
                    associatedBondingCurve: new PublicKey(associatedBondingCurve),
                    associatedUser: ata,
                    user: wallet.publicKey,
                    systemProgram: SystemProgram.programId,
                    tokenProgram: TOKEN_PROGRAM_ID,
                    rent: SYSVAR_RENT_PUBKEY,
                    eventAuthority: PublicKey.findProgramAddressSync([Buffer.from('__event_authority')], PROGRAM_PUBKEY)[0],
                    program: PROGRAM_PUBKEY,
                })
                .instruction();
        });

    const getSellInstruction = (
        connection: Connection,
        wallet: Keypair,
        mint: PublicKey,
        bondingCurve: PublicKey,
        associatedBondingCurve: PublicKey,
        ata: PublicKey,
        tokenAmount: number,
        slippage: number,
    ): Promise<TransactionInstruction> =>
        _useProgram(connection, wallet, async (program) => {
            const liquidityPool = (await program.account.bondingCurve.fetch(bondingCurve, 'processed')) as any;

            const tokenAmountWithDecimals = new BN(tokenAmount * 10 ** TOKEN_DECIMALS);

            const solAmount = tokenAmountWithDecimals.mul(liquidityPool.virtualSolReserves).div(new BN(liquidityPool.virtualTokenReserves));

            const minSolAmount = solAmount.mul(new BN(100 - slippage)).div(new BN(100));

            return program.methods
                .sell(tokenAmountWithDecimals, minSolAmount)
                .accounts({
                    global: GLOBAL_PUBKEY,
                    feeRecipient: FEE_RECIPIENT_PUBKEY,
                    mint: mint,
                    bondingCurve: new PublicKey(bondingCurve),
                    associatedBondingCurve: new PublicKey(associatedBondingCurve),
                    associatedUser: ata,
                    user: wallet.publicKey,
                    systemProgram: SystemProgram.programId,
                    tokenProgram: TOKEN_PROGRAM_ID,
                    rent: SYSVAR_RENT_PUBKEY,
                    eventAuthority: PublicKey.findProgramAddressSync([Buffer.from('__event_authority')], PROGRAM_PUBKEY)[0],
                    program: PROGRAM_PUBKEY,
                })
                .instruction();
        });

    function getTokenAmount(lamportsAmount: number, liquidityPool: any) {
        const totalLiquidity = liquidityPool.virtualSolReserves.mul(liquidityPool.virtualTokenReserves);
        const newSolReserve = liquidityPool.virtualSolReserves.add(new BN(lamportsAmount));
        const pricePerToken = totalLiquidity.div(newSolReserve).add(new BN(1));

        const tokensSold = liquidityPool.virtualTokenReserves.sub(pricePerToken);

        return BN.min(tokensSold, liquidityPool.realTokenReserves);
    }

    return {
        getBuyInstruction,
        getSellInstruction,
    };
};
